import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { addContact } from "@/redux/rtk/features/CRM/contact/contactSlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import {
  useAddLeadMutation,
  useGetLeadQuery,
  useUpdateLeadMutation,
} from "@/redux/rtk/features/leads/leadsApi";
import { useGetLeadSourcesQuery } from "@/redux/rtk/features/leadSource/leadSourceApi";
import { useGetLeadStatusesQuery } from "@/redux/rtk/features/leadStatus/leadStatusApi";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useGetInterestsQuery } from "@/redux/rtk/features/interest/interestApi";

import { Button, DatePicker, Form, Input, InputNumber, Select } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { getSetting } from "@/redux/rtk/features/setting/settingSlice";
import { FaWhatsapp } from "react-icons/fa";
import { COUNTRIES } from "@/utils/countries";
import usePermissions from "@/utils/usePermissions";

export default function UpdateLead({ data, onClose }) {
  const { t } = useTranslation();
  const { id } = data;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const watchedPhone = Form.useWatch("phone", form);

  const { list: staffList, loading: staffLoading } = useSelector(
    (state) => state.users
  );

  const currentUserId = localStorage.getItem("id");
  const currentUserName = localStorage.getItem("user");

  const { data: leadSources, isLoading: leadSourceLoading } =
    useGetLeadSourcesQuery();

  const { data: singleLead, isLoading: leadLoading } = useGetLeadQuery(id);

  const { permissions } = usePermissions();
  const canReadAllUser = permissions?.includes("readAll-user");

  // Create owner options including current user and existing lead owner
  const ownerOptions = (canReadAllUser && staffList) ? [...staffList] : [];

  // Add current user if missing
  const isCurrentUserInList = ownerOptions.some(item => String(item.id) === String(currentUserId));
  if (!isCurrentUserInList && currentUserId && currentUserName) {
    ownerOptions.push({
      id: parseInt(currentUserId),
      username: currentUserName
    });
  }

  // Add existing lead owner if missing
  if (singleLead?.leadOwner) {
    const isOwnerInList = ownerOptions.some(item => String(item.id) === String(singleLead.leadOwner.id));
    if (!isOwnerInList) {
      ownerOptions.push({
        id: singleLead.leadOwner.id,
        username: singleLead.leadOwner.username || `${singleLead.leadOwner.firstName || ""} ${singleLead.leadOwner.lastName || ""}`.trim() || t("common.unknown")
      });
    }
  }

  const { data: leadStatuses, isLoading: leadStatusLoading } =
    useGetLeadStatusesQuery();

  const { data: majors, isLoading: majorLoading } = useGetMajorsQuery();
  const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();
  const { data: interests, isLoading: interestLoading } = useGetInterestsQuery();

  const resolveInterestId = useCallback((value) => {
    if (value === null || value === undefined || value === "") return undefined;
    const byId = interests?.find((item) => String(item.id) === String(value));
    if (byId) return byId.id;
    const byName = interests?.find((item) => String(item.name).toLowerCase() === String(value).toLowerCase());
    return byName?.id;
  }, [interests]);

  const { data: setting } = useSelector((state) => state.setting);
  const [fieldConfig, setFieldConfig] = useState({});

  useEffect(() => {
    if (setting?.leadFieldConfig) {
      try {
        setFieldConfig(JSON.parse(setting.leadFieldConfig));
      } catch (e) {
        console.error("Error parsing lead field config", e);
      }
    }
  }, [setting]);

  const isRequired = (key) => {
    return fieldConfig[key] === undefined ? true : fieldConfig[key];
  };

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
    if (!setting) {
      dispatch(getSetting());
    }
  }, [dispatch, setting]);

  useEffect(() => {
    if (singleLead) {
      form.setFieldsValue({
        ...singleLead,
        language_required: singleLead.languageRequired,
        interest: resolveInterestId(singleLead.interest) || resolveInterestId(singleLead.interestName) || singleLead.interest,
        majorId: singleLead.majorId,
        dateOfBirth: singleLead.dateOfBirth ? dayjs(singleLead.dateOfBirth) : undefined,
      });
    }
  }, [form, singleLead, resolveInterestId]);

  const [updateLeads, { isLoading }] = useUpdateLeadMutation();
  const onFinish = async (values) => {
    const formData = {
      ...values,
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : null,
    };

    const resp = await updateLeads({ id, values: formData });
    if (resp.data) {
      form.resetFields();
      onClose();
    }
  };

  return (
    <div className="mt-5">
      <UserPrivateComponent permission="update-lead" comment={t("common.permissionError")} type="update">
        <Form
          className="w-full"
          onFinish={onFinish}
          colon={false}
          layout="vertical"
          initialValues={singleLead}
          form={form}>
          <div className="w-full">
            {/* Row 1: First Name and Last Name */}
            <div className="flex gap-4">
              <Form.Item
                label={t("common.firstName")}
                name="firstName"
                className="flex-1"
                rules={[
                  { required: isRequired("firstName"), message: t("common.firstNameRequired") || "First name is required" },
                ]}>
                <Input placeholder={t("common.firstName")} />
              </Form.Item>
              <Form.Item
                label={t("common.lastName")}
                name="lastName"
                className="flex-1"
                rules={[
                  { required: isRequired("lastName"), message: t("common.lastNameRequired") || "Last name is required" },
                ]}>
                <Input placeholder={t("common.lastName")} />
              </Form.Item>
            </div>

            {/* Row 2: Email and Date of Birth */}
            <div className="flex gap-4">
              <Form.Item
                label={t("common.email")}
                name="email"
                className="flex-1"
                rules={[{ type: "email", message: t("lead.invalidEmail") }, { required: isRequired("email"), message: t("common.required") }]}>
                <Input placeholder={t("common.emailPlaceholder")} />
              </Form.Item>
              <Form.Item label={t("lead.dateOfBirth")} name="dateOfBirth" className="flex-1" rules={[{ required: isRequired("dateOfBirth"), message: t("common.required") }]}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </div>

            {/* Row 3: Country and Country of Study */}
            <div className="flex gap-4">
              <Form.Item label={t("lead.country")} name="country" className="flex-1" rules={[{ required: isRequired("country"), message: t("common.required") }]}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.country")}>
                  {COUNTRIES.map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label={t("lead.country_of_study")} name="country_of_study" className="flex-1" rules={[{ required: isRequired("country_of_study"), message: t("common.required") }]}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.country_of_study")}>
                  {COUNTRIES.map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 2: Phone Number and Lead Owner */}
            <div className="flex gap-4">
              <Form.Item
                label={t("lead.phoneNumber")}
                className="flex-1"
                required={isRequired("mobile") || isRequired("phone")}
              >
                <div className="flex items-center gap-2">
                  <Form.Item
                    name="phone"
                    noStyle
                    normalize={(value) => value.replace(/[^0-9+]/g, "")}
                    rules={[
                      { required: isRequired("mobile") || isRequired("phone"), message: t("common.required") },
                      { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
                    ]}>
                    <Input placeholder={t("lead.phoneNumber")} style={{ width: "100%" }} />
                  </Form.Item>
                  {watchedPhone && (
                    <a
                      href={`https://wa.me/${watchedPhone.toString().replace(/[^0-9+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                      title="Open in WhatsApp">
                      <FaWhatsapp className="text-lg" />
                    </a>
                  )}
                </div>
              </Form.Item>
              <Form.Item
                label={t("lead.leadOwner")}
                name="leadOwnerId"
                className="flex-1">
                <Select
                  loading={staffLoading}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.selectLeadOwner")}>
                  {ownerOptions?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.username}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 3: Lead Value and Lead Status */}
            <div className="flex gap-4">
              <Form.Item
                label={t("lead.leadStatus")}
                name="leadStatus"
                className="flex-1"
                rules={[{ required: isRequired("leadStatus"), message: t("common.required") }]}>
                <Select
                  loading={leadStatusLoading}
                  allowClear
                  showSearch
                  placeholder={t("lead.selectLeadStatus")}
                  optionLabelProp="label"
                  filterOption={(input, option) =>
                    (option?.labelString ?? "").toLowerCase().includes(input.toLowerCase())
                  }>
                  {leadStatuses?.map((item) => (
                    <Select.Option
                      key={item.id}
                      value={item.name}
                      labelString={item.name || ""}
                      label={
                        <div className="flex items-center gap-2">
                          <span
                            style={{
                              display: "inline-block",
                              width: "12px",
                              height: "12px",
                              backgroundColor: item.color,
                              borderRadius: "2px",
                            }}
                          />
                          {item.name}
                        </div>
                      }>
                      <div className="flex items-center gap-2">
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: item.color,
                            borderRadius: "2px",
                          }}
                        />
                        {item.name}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={t("lead.interest")}
                name="interest"
                className="flex-1"
                rules={[{ required: isRequired("interest"), message: t("lead.interestRequired") }]}>
                <Select
                  loading={interestLoading}
                  allowClear
                  showSearch
                  placeholder={t("lead.selectInterest")}>
                  {interests?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 4: Lead Source and Contact Status */}
            <div className="flex gap-4">
              <Form.Item
                label={t("lead.leadSource")}
                name="leadSourceId"
                className="flex-1"
                rules={[{ required: isRequired("leadSourceId"), message: t("common.required") }]}>
                <Select
                  loading={leadSourceLoading}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.selectLeadSource")}>
                  {leadSources?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={t("lead.requiredLanguage")}
                name="language_required"
                className="flex-1"
                rules={[{ required: isRequired("language_required"), message: t("common.required") }]}>
                <Select
                  allowClear
                  placeholder={t("lead.selectLanguage")}>
                  <Select.Option value="en">{t("lead.languages.en")}</Select.Option>
                  <Select.Option value="tr">{t("lead.languages.tr")}</Select.Option>
                  <Select.Option value="ar">{t("lead.languages.ar")}</Select.Option>
                  <Select.Option value="fr">{t("lead.languages.fr")}</Select.Option>
                  <Select.Option value="ru">{t("lead.languages.ru")}</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                label={t("lead.degree")}
                name="degree"
                className="flex-1"
                rules={[{ required: isRequired("degree"), message: t("common.required") }]}>
                <Select
                  loading={degreeLoading}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.selectDegree")}>
                  {degrees?.map((item) => (
                    <Select.Option key={item.id} value={item.name}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 5: Major */}
            <div className="flex gap-4">
              <Form.Item
                label={t("lead.major")}
                name="majorId"
                className="flex-1"
                rules={[{ required: isRequired("majorId"), message: t("lead.majorRequired") }]}>
                <Select
                  loading={majorLoading}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder={t("lead.selectMajor")}>
                  {majors?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <div className="flex-1" /> {/* Spacer */}
            </div>
          </div>

          {/* Action Buttons */}
          <Form.Item>
            <div className="flex items-center gap-2">
              <Button
                size="large"
                htmlType="submit"
                type="primary"
                loading={isLoading}>
                {t("common.save")}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </UserPrivateComponent>
    </div>
  );
}
